/**
 * AuthModal — native in-app auth (login / signup / forgot-password).
 *
 * Renders only while the AuthContext modal is open (which only happens when the
 * `nativeAuthEnabled` super-admin toggle is on; otherwise login()/signup()
 * redirect to Keycloak's hosted pages).
 *
 *   login  → BFF ROPC (loginWithPassword). Social buttons redirect (kc_idp_hint).
 *            A staff email domain (src/config/staffAuth.ts) skips the password
 *            field and redirects to the Microsoft 365 broker instead — routing
 *            only, never an authorization check.
 *   signup → emails a 4-character code (nativeSignup); the account is created
 *            only once that code is confirmed, and the user is logged straight in.
 *            Staff domains cannot self-register at all — the server refuses
 *            (403 staff_domain) and this form redirects them to the broker.
 *   forgot → emails a 4-character code (nativeResetRequest); verify it, set a new
 *            password, and you're logged in — all in this modal.
 *
 * Every email comes from the app's own mailer. Keycloak sends nothing and there
 * is no emailed-link flow to fall back to (docs/KEYCLOAK-REALM-LOCKDOWN.md), so
 * nothing here ever leaves the page.
 *
 * Inherits partner branding via the global --partner-* CSS variables.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { X, Lock, Eye, EyeOff, MailCheck } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { nativeSignup, nativeResendCode, nativeResetRequest, nativeResetVerify, checkEmailExists } from '../../services/api';
import { PasswordField } from './PasswordField';
import { CodeInput } from './CodeInput';
import { SocialButtons } from './socialBrands';
import { isStaffEmail, STAFF_IDP_ALIAS, STAFF_SIGN_IN_NOTICE_KEY } from '../../config/staffAuth';
import { authErrorMessage, networkErrorMessage } from '../../config/authErrors';

const API_URL = process.env.REACT_APP_API_URL || '';

type View = 'login' | 'signup' | 'setPassword' | 'forgot' | 'code' | 'resetCode' | 'resetPassword';

export const AuthModal: React.FC = () => {
  const { t } = useTranslation('auth');
  const { authModalOpen, authModalView, authModalEmail, closeAuthModal, loginWithPassword, completePasswordChange, verifySignupCode, completePasswordReset } = useAuth();

  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [view, setView] = useState<View>('login');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwValid, setPwValid] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailTaken, setEmailTaken] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);

  // Staff sign-in redirect (Microsoft 365 broker). The ref is the real guard —
  // blur and submit can both fire for one interaction and state updates too late
  // to stop the second; the flag only drives the button label.
  const staffRedirecting = useRef(false);
  const [staffRedirectingUI, setStaffRedirectingUI] = useState(false);

  // Signup code (OTP) flow.
  const [codeError, setCodeError] = useState<string | null>(null);
  const [codeInvalid, setCodeInvalid] = useState(false);
  const [codeClearSignal, setCodeClearSignal] = useState(0);
  const [codeExpiresAt, setCodeExpiresAt] = useState<number | null>(null);
  const [nowTick, setNowTick] = useState(Date.now());
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);

  // Mount/fade lifecycle.
  useEffect(() => {
    if (authModalOpen) {
      setMounted(true);
      const raf = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(raf);
    }
    setVisible(false);
    const timer = setTimeout(() => setMounted(false), 200);
    return () => clearTimeout(timer);
  }, [authModalOpen]);

  // Reset to the requested view + clear transient state on each open.
  useEffect(() => {
    if (authModalOpen) {
      setView(authModalView);
      if (authModalEmail) setEmail(authModalEmail);
      setError(null);
      setPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPwValid(false);
      setShowPassword(false);
      setSubmitting(false);
      setEmailTaken(false);
      setCheckingEmail(false);
      setCodeError(null);
      setCodeInvalid(false);
      setCodeExpiresAt(null);
      setVerifyingCode(false);
      setResendingCode(false);
      staffRedirecting.current = false;
      setStaffRedirectingUI(false);
    }
  }, [authModalOpen, authModalView, authModalEmail]);

  // Countdown ticker — runs while a code view is open with a live expiry.
  useEffect(() => {
    if ((view !== 'code' && view !== 'resetCode') || codeExpiresAt === null) return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [view, codeExpiresAt]);

  // Signup: debounced "is this email already registered?" check. Fires once the
  // address is well-formed; flags the field + blocks submit if it already exists.
  useEffect(() => {
    if (!authModalOpen || view !== 'signup') { setEmailTaken(false); setCheckingEmail(false); return; }
    const e = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { setEmailTaken(false); setCheckingEmail(false); return; }
    // Staff domains can't sign up at all — no point asking whether it's taken.
    if (isStaffEmail(e)) { setEmailTaken(false); setCheckingEmail(false); return; }
    let cancelled = false;
    setCheckingEmail(true);
    const timer = setTimeout(async () => {
      const { exists, checked } = await checkEmailExists(e);
      if (cancelled) return;
      setCheckingEmail(false);
      setEmailTaken(checked && exists);
    }, 500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [authModalOpen, view, email]);

  // Esc to close.
  useEffect(() => {
    if (!authModalOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeAuthModal(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [authModalOpen, closeAuthModal]);

  if (!mounted) return null;

  const switchView = (v: View) => { setError(null); setView(v); };

  /** Reset the code boxes and restart the expiry countdown for a freshly sent code. */
  const showCodeScreen = (expiresIn?: number) => {
    setCodeError(null);
    setCodeInvalid(false);
    setCodeClearSignal((n) => n + 1);
    setCodeExpiresAt(Date.now() + (expiresIn ?? 900) * 1000);
    setNowTick(Date.now());
  };

  const socialHref = (idp: string) => {
    const dest = window.location.pathname + window.location.search;
    return `${API_URL}/api/auth-login.php?kc_idp_hint=${encodeURIComponent(idp)}&return_to=${encodeURIComponent(dest)}`;
  };

  /**
   * A recognised staff domain never sees the password form — hand the browser to
   * Keycloak with the Microsoft 365 hint and let the normal callback sign them in.
   *
   * Fires on submit, not on the email field's blur: blur also fires when the user
   * clicks the close button, the backdrop, or "Create an account", and navigating
   * away from those would be exactly the hijack this is meant to avoid. The form
   * already reshapes itself as the domain is typed, so submit is the natural point.
   * Guarded because a double Enter/click can submit twice.
   */
  const goToStaffLogin = () => {
    if (staffRedirecting.current) return;
    staffRedirecting.current = true;
    setStaffRedirectingUI(true);
    window.location.assign(socialHref(STAFF_IDP_ALIAS));
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (isStaffEmail(email)) { goToStaffLogin(); return; }
    setError(null);
    setSubmitting(true);
    const res = await loginWithPassword(email.trim(), password);
    setSubmitting(false);
    if (res.ok) return; // context closes the modal
    if (res.passwordChangeRequired) {
      // Valid temp/expired password — collect a new one in-app (no hosted double-login).
      setNewPassword('');
      setConfirmPassword('');
      setPwValid(false);
      switchView('setPassword');
      return;
    }
    if (res.emailVerification) {
      // Password was right, but the address was never confirmed (a legacy or
      // social account). The server has emailed a code — same screen as signup.
      showCodeScreen(res.expiresIn);
      switchView('code');
      return;
    }
    setError(authErrorMessage(res, 'auth:errors.signInFailed'));
  };

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !pwValid) return;
    setError(null);
    setSubmitting(true);
    // `password` still holds the temp password carried over from the login form.
    const res = await completePasswordChange(email.trim(), password, newPassword);
    setSubmitting(false);
    if (!res.ok) setError(authErrorMessage(res, 'auth:errors.passwordUpdateFailed'));
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    // Staff domains cannot self-register (the server refuses with 403
    // staff_domain); send them to the Microsoft 365 broker instead.
    if (isStaffEmail(email)) { goToStaffLogin(); return; }
    if (!pwValid) {
      setError(t('errors.choosePassword'));
      return;
    }
    setError(null);
    setSubmitting(true);
    let res;
    try {
      res = await nativeSignup({
        email: email.trim(), password, firstName: firstName.trim(), lastName: lastName.trim(),
      });
    } catch {
      setSubmitting(false);
      setError(networkErrorMessage());
      return;
    }
    setSubmitting(false);
    if (res.status === 'code_sent') {
      showCodeScreen(res.expiresIn);
      setView('code');
    } else {
      setError(authErrorMessage(res, 'auth:errors.createAccountFailed'));
    }
  };

  const handleVerifyCode = async (code: string) => {
    if (verifyingCode) return;
    setCodeError(null);
    setCodeInvalid(false);
    setVerifyingCode(true);
    const res = await verifySignupCode(code);
    setVerifyingCode(false);
    if (res.ok) return; // context sets the user + closes the modal
    setCodeInvalid(true);
    setCodeClearSignal((n) => n + 1); // clear boxes for another try
    if (res.error === 'expired' || res.error === 'too_many') setCodeExpiresAt(0);
    setCodeError(authErrorMessage(res, 'auth:errors.codeIncorrect'));
  };

  const handleResendCode = async () => {
    if (resendingCode) return;
    setResendingCode(true);
    setCodeError(null);
    let res;
    try {
      res = await nativeResendCode();
    } catch {
      setResendingCode(false);
      setCodeError(networkErrorMessage());
      return;
    }
    setResendingCode(false);
    if (res.ok) {
      showCodeScreen(res.expiresIn);
    } else {
      setCodeError(authErrorMessage(res, 'auth:errors.resendFailed'));
    }
  };

  /**
   * Only `code_sent` advances to the code screen. Anything else — a throttle, a
   * 503 with no mailer, a dead network — is surfaced as an error. Showing the
   * "we emailed you a code" screen on a failed request would leave the user
   * waiting for a message that was never sent.
   */
  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    let res;
    try {
      res = await nativeResetRequest(email.trim());
    } catch {
      setSubmitting(false);
      setError(networkErrorMessage());
      return;
    }
    setSubmitting(false);
    if (res.status === 'code_sent') {
      showCodeScreen(res.expiresIn);
      setView('resetCode');
      return;
    }
    setError(authErrorMessage(res, 'auth:errors.resetCodeFailed'));
  };

  const handleResetVerify = async (code: string) => {
    if (verifyingCode) return;
    setCodeError(null);
    setCodeInvalid(false);
    setVerifyingCode(true);
    const res = await nativeResetVerify(code);
    setVerifyingCode(false);
    if (res.ok) {
      // Code confirmed → collect the new password.
      setNewPassword('');
      setConfirmPassword('');
      setPwValid(false);
      setError(null);
      setView('resetPassword');
      return;
    }
    setCodeInvalid(true);
    setCodeClearSignal((n) => n + 1);
    if (res.error === 'expired' || res.error === 'too_many') setCodeExpiresAt(0);
    setCodeError(authErrorMessage(res, 'auth:errors.codeIncorrect'));
  };

  const handleResetResend = async () => {
    if (resendingCode) return;
    setResendingCode(true);
    setCodeError(null);
    let res;
    try {
      res = await nativeResetRequest(email.trim());
    } catch {
      setResendingCode(false);
      setCodeError(networkErrorMessage());
      return;
    }
    setResendingCode(false);
    if (res.status === 'code_sent') {
      showCodeScreen(res.expiresIn);
    } else {
      setCodeError(authErrorMessage(res, 'auth:errors.resendFailed'));
    }
  };

  const handleResetComplete = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !pwValid) return;
    setError(null);
    setSubmitting(true);
    const res = await completePasswordReset(newPassword);
    setSubmitting(false);
    if (!res.ok) setError(authErrorMessage(res, 'auth:errors.resetFailed'));
  };

  // Detected live so the explanatory line and the missing password fields appear
  // while the address is still being typed — the redirect itself waits for submit.
  // Sign-in and signup both route to the broker (staff have no password account
  // and cannot create one); the reset views keep their normal flow.
  const staffEmail = (view === 'login' || view === 'signup') && isStaffEmail(email);

  const title = view === 'signup' ? t('titles.signup')
    : view === 'setPassword' || view === 'resetPassword' ? t('titles.setPassword')
    : view === 'forgot' ? t('titles.forgot')
    : view === 'code' || view === 'resetCode' ? t('titles.code')
    : t('titles.login');

  const primaryBtnStyle: React.CSSProperties = {
    background: 'var(--partner-button-bg, #F5A623)',
    color: 'var(--partner-button-fg, #14233a)',
    border: 'none',
  };

  // Countdown display for the code view.
  const secondsLeft = codeExpiresAt === null ? null : Math.max(0, Math.round((codeExpiresAt - nowTick) / 1000));
  const codeExpired = secondsLeft !== null && secondsLeft <= 0;
  const mmss = secondsLeft === null ? '' :
    `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-modal-title"
      onMouseDown={closeAuthModal}
      style={{
        position: 'fixed', inset: 0, zIndex: 1080, display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 16,
        background: 'rgba(0,0,0,0.55)', opacity: visible ? 1 : 0, transition: 'opacity 200ms ease',
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 420, background: '#fff', borderRadius: 12,
          boxShadow: '0 20px 60px rgba(0,0,0,0.35)', overflow: 'hidden',
          transform: visible ? 'translateY(0) scale(1)' : 'translateY(8px) scale(0.98)',
          transition: 'transform 200ms ease',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 20px',
            background: 'var(--partner-modal-header-bg, #14233a)',
            color: 'var(--partner-modal-header-fg, #ffffff)',
          }}
        >
          <span id="auth-modal-title" className="d-flex align-items-center gap-2 fw-semibold">
            <Lock size={18} /> {title}
          </span>
          <button
            type="button" aria-label={t('close')} onClick={closeAuthModal}
            className="btn btn-sm p-1 d-flex"
            style={{ color: 'inherit', background: 'transparent', border: 'none', lineHeight: 1 }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: 20 }}>
          {/* ---------- LOGIN ---------- */}
          {view === 'login' && (
            <>
              <form onSubmit={handleLogin} noValidate>
                <div className="mb-3">
                  <label htmlFor="auth-email" className="form-label small fw-semibold mb-1">{t('fields.email')}</label>
                  <input id="auth-email" type="email" className="form-control" autoComplete="username"
                    autoFocus required value={email} disabled={submitting}
                    onChange={(e) => setEmail(e.target.value)} />
                </div>
                {staffEmail ? (
                  <p className="text-muted small mb-3" role="status">{t(STAFF_SIGN_IN_NOTICE_KEY)}</p>
                ) : (
                  <>
                    <div className="mb-2">
                      <label htmlFor="auth-password" className="form-label small fw-semibold mb-1">{t('fields.password')}</label>
                      <div className="input-group">
                        <input id="auth-password" type={showPassword ? 'text' : 'password'} className="form-control"
                          autoComplete="current-password" required value={password} disabled={submitting}
                          onChange={(e) => setPassword(e.target.value)} />
                        <button type="button" className="btn btn-outline-secondary" tabIndex={-1}
                          aria-label={showPassword ? t('password.hide') : t('password.show')}
                          onClick={() => setShowPassword((s) => !s)}>
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                    <div className="d-flex justify-content-end mb-3">
                      <button type="button" className="btn btn-link btn-sm p-0" onClick={() => switchView('forgot')}>
                        {t('login.forgot')}
                      </button>
                    </div>
                  </>
                )}
                {error && <div className="alert alert-danger py-2 px-3 small mb-3" role="alert">{error}</div>}
                <button type="submit" className="btn w-100 fw-semibold"
                  disabled={submitting || !email.trim() || (!staffEmail && !password) || staffRedirectingUI}
                  style={primaryBtnStyle}>
                  {staffEmail ? (staffRedirectingUI ? t('redirecting') : t('continue'))
                    : submitting ? t('login.busy') : t('login.submit')}
                </button>
              </form>

              <SocialButtons hrefFor={socialHref} disabled={submitting} />

              <p className="text-center small text-muted mt-3 mb-0">
                {t('login.newUser')}{' '}
                <button type="button" className="btn btn-link btn-sm p-0 align-baseline"
                  onClick={() => switchView('signup')}>{t('login.createAccount')}</button>
              </p>
            </>
          )}

          {/* ---------- SIGNUP ---------- */}
          {view === 'signup' && (
            <form onSubmit={handleSignup} noValidate>
              <div className="row g-2 mb-3">
                <div className="col">
                  <label htmlFor="su-first" className="form-label small fw-semibold mb-1">{t('fields.firstName')}</label>
                  <input id="su-first" type="text" className="form-control" autoComplete="given-name"
                    autoFocus required value={firstName} disabled={submitting}
                    onChange={(e) => setFirstName(e.target.value)} />
                </div>
                <div className="col">
                  <label htmlFor="su-last" className="form-label small fw-semibold mb-1">{t('fields.lastName')}</label>
                  <input id="su-last" type="text" className="form-control" autoComplete="family-name"
                    required value={lastName} disabled={submitting}
                    onChange={(e) => setLastName(e.target.value)} />
                </div>
              </div>
              <div className="mb-3">
                <label htmlFor="su-email" className="form-label small fw-semibold mb-1">{t('fields.email')}</label>
                <input id="su-email" type="email"
                  className={`form-control ${emailTaken ? 'is-invalid' : ''}`} autoComplete="email"
                  required value={email} disabled={submitting}
                  onChange={(e) => setEmail(e.target.value)} />
                {checkingEmail && <div className="form-text">{t('signup.checkingEmail')}</div>}
                {emailTaken && (
                  <div className="invalid-feedback d-block">
                    {t('errors.emailExists')}{' '}
                    <button type="button" className="btn btn-link btn-sm p-0 align-baseline"
                      onClick={() => switchView('login')}>{t('signup.signInInstead')}</button>
                  </div>
                )}
              </div>
              {staffEmail ? (
                <p className="text-muted small mb-3" role="status">{t(STAFF_SIGN_IN_NOTICE_KEY)}</p>
              ) : (
                <PasswordField
                  idPrefix="su"
                  value={password}
                  onChange={setPassword}
                  confirmValue={confirmPassword}
                  onConfirmChange={setConfirmPassword}
                  disabled={submitting}
                  onValidChange={setPwValid}
                />
              )}
              {error && <div className="alert alert-danger py-2 px-3 small mb-3" role="alert">{error}</div>}
              <button type="submit" className="btn w-100 fw-semibold"
                disabled={submitting || !email.trim() || staffRedirectingUI
                  || (!staffEmail && (!pwValid || !firstName.trim() || !lastName.trim() || emailTaken))}
                style={primaryBtnStyle}>
                {staffEmail ? (staffRedirectingUI ? t('redirecting') : t('continue'))
                  : submitting ? t('signup.busy') : t('signup.submit')}
              </button>

              <SocialButtons hrefFor={socialHref} disabled={submitting} />

              <p className="text-center small text-muted mt-3 mb-0">
                {t('signup.haveAccount')}{' '}
                <button type="button" className="btn btn-link btn-sm p-0 align-baseline"
                  onClick={() => switchView('login')}>{t('titles.login')}</button>
              </p>
            </form>
          )}

          {/* ---------- SET NEW PASSWORD (forced temp-password change) ---------- */}
          {view === 'setPassword' && (
            <form onSubmit={handleSetPassword} noValidate>
              <p className="text-muted small mb-3">
                <Trans i18nKey="setPassword.intro" ns="auth"
                  values={{ email: email.trim() }} components={{ b: <strong /> }} />
              </p>
              <PasswordField
                idPrefix="np"
                label={t('fields.newPassword')}
                value={newPassword}
                onChange={setNewPassword}
                confirmValue={confirmPassword}
                onConfirmChange={setConfirmPassword}
                disabled={submitting}
                autoFocus
                onValidChange={setPwValid}
              />
              {error && <div className="alert alert-danger py-2 px-3 small mb-3" role="alert">{error}</div>}
              <button type="submit" className="btn w-100 fw-semibold"
                disabled={submitting || !pwValid} style={primaryBtnStyle}>
                {submitting ? t('setPassword.busy') : t('setPassword.submit')}
              </button>
            </form>
          )}

          {/* ---------- CODE (in-app email verification) ---------- */}
          {view === 'code' && (
            <div className="text-center">
              <MailCheck size={38} className="mb-3" style={{ color: 'var(--partner-primary, #14233a)' }} />
              <p className="mb-1">{t('code.emailedTo', { count: 4 })}</p>
              <p className="fw-semibold mb-3">{email.trim()}</p>

              <CodeInput
                length={4}
                clearSignal={codeClearSignal}
                invalid={codeInvalid}
                disabled={verifyingCode || codeExpired}
                onComplete={handleVerifyCode}
              />

              <div className="mt-3" style={{ minHeight: 24 }}>
                {verifyingCode ? (
                  <span className="text-muted small">{t('code.verifying')}</span>
                ) : codeError ? (
                  <span className="small text-danger">{codeError}</span>
                ) : codeExpired ? (
                  <span className="small text-danger">{t('code.expired')}</span>
                ) : secondsLeft !== null ? (
                  <span className="small text-muted">{t('code.expiresIn', { time: mmss })}</span>
                ) : null}
              </div>

              <div className="mt-2">
                <button type="button" className="btn btn-link btn-sm" onClick={handleResendCode}
                  disabled={resendingCode}>
                  {resendingCode ? t('code.sending') : t('code.resend')}
                </button>
              </div>

              <p className="text-center small text-muted mt-2 mb-0">
                {t('code.wrongEmail')}{' '}
                <button type="button" className="btn btn-link btn-sm p-0 align-baseline"
                  onClick={() => switchView('signup')}>{t('code.startOver')}</button>
              </p>
            </div>
          )}

          {/* ---------- RESET: CODE ---------- */}
          {view === 'resetCode' && (
            <div className="text-center">
              <MailCheck size={38} className="mb-3" style={{ color: 'var(--partner-primary, #14233a)' }} />
              <p className="mb-1">{t('code.emailedToIfExists', { count: 4 })}</p>
              <p className="fw-semibold mb-3">{email.trim()}</p>

              <CodeInput
                length={4}
                clearSignal={codeClearSignal}
                invalid={codeInvalid}
                disabled={verifyingCode || codeExpired}
                onComplete={handleResetVerify}
              />

              <div className="mt-3" style={{ minHeight: 24 }}>
                {verifyingCode ? (
                  <span className="text-muted small">{t('code.verifying')}</span>
                ) : codeError ? (
                  <span className="small text-danger">{codeError}</span>
                ) : codeExpired ? (
                  <span className="small text-danger">{t('code.expired')}</span>
                ) : secondsLeft !== null ? (
                  <span className="small text-muted">{t('code.expiresIn', { time: mmss })}</span>
                ) : null}
              </div>

              <div className="mt-2">
                <button type="button" className="btn btn-link btn-sm" onClick={handleResetResend}
                  disabled={resendingCode}>
                  {resendingCode ? t('code.sending') : t('code.resend')}
                </button>
              </div>

              <p className="text-center small text-muted mt-2 mb-0">
                <button type="button" className="btn btn-link btn-sm p-0 align-baseline"
                  onClick={() => switchView('login')}>{t('backToSignIn')}</button>
              </p>
            </div>
          )}

          {/* ---------- RESET: NEW PASSWORD ---------- */}
          {view === 'resetPassword' && (
            <form onSubmit={handleResetComplete} noValidate>
              <p className="text-muted small mb-3">
                <Trans i18nKey="resetPassword.intro" ns="auth"
                  values={{ email: email.trim() }} components={{ b: <strong /> }} />
              </p>
              <PasswordField
                idPrefix="rp"
                label={t('fields.newPassword')}
                value={newPassword}
                onChange={setNewPassword}
                confirmValue={confirmPassword}
                onConfirmChange={setConfirmPassword}
                disabled={submitting}
                autoFocus
                onValidChange={setPwValid}
              />
              {error && <div className="alert alert-danger py-2 px-3 small mb-3" role="alert">{error}</div>}
              <button type="submit" className="btn w-100 fw-semibold"
                disabled={submitting || !pwValid} style={primaryBtnStyle}>
                {submitting ? t('setPassword.busy') : t('resetPassword.submit')}
              </button>
            </form>
          )}

          {/* ---------- FORGOT ---------- */}
          {view === 'forgot' && (
            <form onSubmit={handleForgot} noValidate>
              <p className="text-muted small mb-3">{t('forgot.intro')}</p>
              <div className="mb-3">
                <label htmlFor="fp-email" className="form-label small fw-semibold mb-1">{t('fields.email')}</label>
                <input id="fp-email" type="email" className="form-control" autoComplete="email"
                  autoFocus required value={email} disabled={submitting}
                  onChange={(e) => setEmail(e.target.value)} />
              </div>
              {error && <div className="alert alert-danger py-2 px-3 small mb-3" role="alert">{error}</div>}
              <button type="submit" className="btn w-100 fw-semibold"
                disabled={submitting || !email.trim()} style={primaryBtnStyle}>
                {submitting ? t('code.sending') : t('forgot.submit')}
              </button>
              <p className="text-center small text-muted mt-3 mb-0">
                <button type="button" className="btn btn-link btn-sm p-0 align-baseline"
                  onClick={() => switchView('login')}>{t('backToSignIn')}</button>
              </p>
            </form>
          )}

        </div>
      </div>
    </div>
  );
};
